import React, { ReactElement, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ProfileViewModeBg from "../../assets/images&icons/bg-login.jpg";
import { axios } from '../..';
import AuthContext from "../../store/AuthContext";
import EyeOpen from "../../components/Atomos/Icons/EyeOpen";
import EyeClosed from "../../components/Atomos/Icons/EyeClosed";
import css from "../../components/Forms/LoginForm.module.css";
import { setCookie } from "../../utils/cookies";
import { resolveAuthErrorPayload } from "../../utils/authErrorPayload";
import { getAndClearRedirectPath } from "../../utils/logout";
import { clearApplicationCache, redirectAfterLogin } from "../../utils/clearApplicationCache";
import jwtDecode from "jwt-decode";

type SocialProviders = "Google" | "LinkedIn" | null;

function SimpleLogin(): ReactElement {
  const { setIsAuthenticated } = useContext(AuthContext);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [userName, setUserName] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [pendingSocialAccountLinking, setPendingSocialAccountLinking] = useState<SocialProviders>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string>("");


  useEffect(function setupSocialAccountLinking() {
    const socialProvider = location.state?.socialProvider;
    setPendingSocialAccountLinking(socialProvider);
  }, [location.state]);

  useEffect(() => {
    const message = new URLSearchParams(location.search).get("error");
    if (message) {
      setErrorMessage(message);
    }
  }, [location.search]);

  //HTTP Calls

  // Form Logic
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let response;
      let validToken = false;

      while (!validToken) {
        response = await authenticate();
        if (!response) {
          return;
        }
        
        if (response.id_token) {
          validToken = validateToken(response.id_token);
        }
        if (response.twoFactorAuthenticationNeeded) {
          validToken = true;
        }
      }
      
      handleSocialAccountLinking();
      checkTwoFactorAuthentication(response);
      if (response.id_token) {
        await clearApplicationCache();
        setCookie("token", response.id_token, 0);
        setCookie("token_decoded", JSON.stringify(jwtDecode(response.id_token)), 0);
        setIsAuthenticated(true);
        
        const redirectPath = getAndClearRedirectPath();
        if (redirectPath) {
          redirectAfterLogin(redirectPath);
        } else {
          redirectAfterLogin("/mywsg");
        }
      }
    } catch (error: any) {
      console.error("Error logging in", error);
      return;
    }
  };

  async function authenticate(): Promise<any> {
    try {
      const response = await axios.Post(`/login?socialProvider=${pendingSocialAccountLinking}`, {
        username: userName,
        password: password,
      });

      if (!response || !response.data) {
        return null;
      }

      return response.data;
    } catch (error: any) {
      const resolved = resolveAuthErrorPayload(error.response?.data);
      setErrorMessage(
        resolved.message ||
          (typeof error.response?.data === "string"
            ? error.response.data
            : "") ||
          error.message ||
          "Login failed",
      );
      console.error("Error logging in", error);
      return null;
    }
  }

  function checkTwoFactorAuthentication(response: any) {
    if (response.twoFactorAuthenticationNeeded) {
      sessionStorage.setItem("pendingLogin", JSON.stringify({
        username: userName,
        password: password,
        twoFactorAuthenticationNeeded: true
      }));
      navigate("/two-factor-auth", { replace: true });
    }
    return false
  }

  function validateToken(id_token: string) {
    const ExclusiveWords = ["end", "sql", "drop"];
    if (ExclusiveWords.some((word) => id_token.includes(word))) {
      // Retry login process
      return false;
    }
    return true;
  }

  function handleSocialAccountLinking() {
    //Main file will handle the social account linking after the login is successful
    sessionStorage.setItem('pendingSocialAccountLinking', pendingSocialAccountLinking as string);
  }

  useEffect(() => {
    const handleGoBack = () => {
      navigate("/login", { replace: true });
    };

    window.addEventListener("popstate", () => {
      handleGoBack();
    });

    return () => {
      window.removeEventListener("popstate", handleGoBack);
    };
  }, [navigate]);

  return (
    <>
      <div
        className="absolute w-full min-h-screen"
        style={{
          backgroundImage: `url(${ProfileViewModeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
        }}
      ></div>
      <div className="w-full min-h-screen justify-center items-center flex self-stretch relative">
        <div className="w-8/12 lg:w-[520px]">
          <div
            className={`flex w-full align-middle justify-center gap-28 px-5 py-4 bg-[#9C3325]`}
          >
            <h2
              className={`text-center text-xl font-sans text-white roboto-font`}
            >
              LINK ACCOUNT LOGIN
            </h2>
          </div>
          <div className="bg-white w-full py-4">
            {errorMessage && <div className="mx-5 bg-red-100 border border-red-400 text-red-700 p-3 rounded-md">
              <span className="text-[1.125rem] mb-2 font-sans px-5 ">{errorMessage}</span>
            </div>}
            <form
              className="text-black py-2 flex flex-col gap-4"
              onSubmit={handleSubmit}
            >
              <span className="text-[1.125rem] mb-2 font-sans px-5 ">Please enter your credentials to link your {pendingSocialAccountLinking} account.</span>
              <div className="text-black pb-8 pt-2 gap-4 flex flex-col">
                <div className="w-full px-5 ">
                  <input
                    type="text"
                    placeholder="Username"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                  />
                </div>
                <div className="w-full px-5">
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOpen /> : <EyeClosed />}
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  className={`w-11/12 text-white mx-auto flex text-center align-middle justify-center py-4 inter_font ${css.buttonWsg}`}
                >
                  LOGIN
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default SimpleLogin;
